import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { LoginRequest } from '../types';
import { useTranslation } from 'react-i18next';

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);

  const validateForm = (): boolean => {
    if (!email.trim()) {
      setError(t('auth.emailRequired'));
      setShowError(true);
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('auth.emailInvalid'));
      setShowError(true);
      return false;
    }
    if (!password.trim()) {
      setError(t('auth.passwordRequired'));
      setShowError(true);
      return false;
    }
    if (password.length < 6) {
      setError(t('auth.passwordMin'));
      setShowError(true);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const credentials: LoginRequest = { email, password, role: 'ADMIN' };
      await login(credentials);

      // Explicitly trigger Chrome credential save for SPA login
      if (window.PasswordCredential) {
        try {
          const cred = new window.PasswordCredential({ id: email, password });
          await navigator.credentials.store(cred);
        } catch (_) { /* ignore if credential API fails */ }
      }

      navigate('/admin/dashboard');
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        t('auth.loginFailed');
      setError(message);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 40%, #0f3460 70%, #533483 100%)',
        p: 2,
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 3,
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          overflow: 'visible',
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #533483 100%)',
            py: 4,
            px: 3,
            textAlign: 'center',
            borderRadius: '12px 12px 0 0',
          }}
        >
          <SecurityIcon sx={{ fontSize: 48, color: '#ffffff', mb: 1 }} />
          <Typography
            variant="h5"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              letterSpacing: '1px',
            }}
          >
            {t('admin.panel')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.6)',
              mt: 0.5,
            }}
          >
            {t('admin.systemManagement')}
          </Typography>
        </Box>

        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSubmit} autoComplete="on">
            <TextField
              fullWidth
              label={t('auth.emailAddress')}
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: '#9e9e9e' }} />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              autoComplete="username email"
            />

            <TextField
              fullWidth
              label={t('auth.password')}
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: '#9e9e9e' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              autoComplete="current-password"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                background: 'linear-gradient(135deg, #1a1a2e 0%, #533483 100%)',
                boxShadow: '0 4px 15px rgba(83,52,131,0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #16213e 0%, #6a4c93 100%)',
                  boxShadow: '0 6px 20px rgba(83,52,131,0.5)',
                },
                '&:disabled': {
                  background: '#bdbdbd',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: '#ffffff' }} />
              ) : (
                t('auth.signIn')
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Snackbar
        open={showError}
        autoHideDuration={5000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminLogin;
